import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import axios from 'axios';
import { getPhoneError, normalizePhoneForSubmit } from './phoneValidation';
import { COUNTRY_OPTIONS, countryOptionFor } from './countries';
import { companyIdMissingList } from './companyId';
import { deriveWorkPermitIssueDate, isStoredFirstCard, issueDateHelper, looksLikeFirstCard } from './labourCard';
import DocSampleBlock from './DocSampleBlock';
import { dubaiYMD } from '../../../utils/dubaiTime';
import './addTeamMember.css';

// 0 = Sunday .. 6 = Saturday, matching Date.getDay() and the stored column.
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ICP_PHOTO_GUIDELINES_URL = 'https://icp.gov.ae/wp-content/uploads/2021/11/icao_english.pdf';

// One current file per type — re-upload replaces (staff_documents upsert).
const DOCUMENT_ROWS = [
    { type: 'personal_photo', label: 'Professional personal photo', starred: true, guidelines: true, note: 'No selfies — professional photos only.' },
    { type: 'passport', label: 'Passport photo', starred: true, note: 'High-quality photo taken flat from directly above (not at an angle) or scanned — all four corners of the passport must be visible.' },
    {
        type: 'last_visa',
        label: 'Residency Visa / Entry Permit / Cancellation',
        starred: true,
        options: [
            { kind: 'residence_visa', text: 'Already on HHC company ID? Upload the residence visa.' },
            { kind: 'cancellation_paper', text: 'Cancelled from a previous company? Upload the cancellation paper.' },
            { kind: 'tourist_entry_permit', text: 'On a tourist visa? Upload the tourist entry permit.' },
        ],
    },
    { type: 'labour_card', label: 'Labour card', samples: [{ kind: 'labour_card', label: 'Labour card' }] },
    { type: 'eid_front', label: 'Emirates ID front' },
    { type: 'eid_back', label: 'Emirates ID back' },
    { type: 'health_insurance_card', label: 'Health insurance card' },
    { type: 'driving_license_front', label: 'Driving license front', driverOnly: true },
    { type: 'driving_license_back', label: 'Driving license back', driverOnly: true },
];

// Date columns arrive as ISO timestamps — <input type="date"> needs YYYY-MM-DD.
// ITEM 8: created_at is a timestamptz INSTANT ('...Z'), so a raw slice(0,10)
// shows the UTC day — one day early for Dubai creations between 00:00 and
// 04:00. dubaiYMD converts instants to the Asia/Dubai calendar day and passes
// date-only strings (DATE columns like date_of_birth) through unchanged.
const toDateInput = (value) => dubaiYMD(value);

// Rendered inside Team.js's modal shell; the modal header owns the title/close X.
// `payroll` (optional) is passed when opened from the Salaries page:
// { entryId, monthLabel }. It adds a scope selector so a salary change can apply
// to this month's payroll snapshot as well as the profile default going forward.
function EditTeamMember({ memberId: id, onClose, onSaved, payroll = null }) {
    const [salaryScope, setSalaryScope] = useState('this'); // 'this' | 'next'
    // First Labour Card date is locked (auto-filled, never overwritten) unless the
    // user explicitly unlocks it to fix a wrong value.
    const [overrideServiceStart, setOverrideServiceStart] = useState(false);
    // Archive is a two-field confirmation DIALOG (last working day + confirm) —
    // never an "armed" button pointing at a field elsewhere in the form.
    const [archiveConfirm, setArchiveConfirm] = useState(false);
    const [archiveDate, setArchiveDate] = useState('');
    const [formData, setFormData] = useState({
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        role: '',
        eid: '',
        phone_number: '',
        calling_number: '',
        created_at: '',
        does_have_id: false,
        does_have_id_from_company: false,
        is_trial: false,
        password: '',
        date_of_birth: '',
        place_of_birth: '',
        passport_number: '',
        unified_number: '',
        passport_issue_date: '',
        passport_expiry_date: '',
        passport_issue_country: '',
        passport_issue_place: '',
        eid_expiry_date: '',
        mohre_person_code: '',
        work_permit_number: '',
        work_permit_issue_date: '',
        work_permit_expiry_date: '',
        service_start_date: '',
        last_working_day: '',
        health_insurance_expiry: '',
        mohre_salary: '',
        contractual_day_off: '',
        conditional_bonus: '',
    });
    const [documents, setDocuments] = useState({});
    const [docArchives, setDocArchives] = useState({}); // doc_type -> [{file_url, archived_at}] newest first
    const [docStatus, setDocStatus] = useState({});
    const [roleOptions, setRoleOptions] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [dialCodes, setDialCodes] = useState({ phone_number: '971', calling_number: '971' });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [targetRoleName, setTargetRoleName] = useState('');
    const [isArchived, setIsArchived] = useState(false);
    const [firstLabourCard, setFirstLabourCard] = useState(false);
    const [firstCardOnRecord, setFirstCardOnRecord] = useState(false);
    const [accessDenied, setAccessDenied] = useState(false);

    // Retrieve user role from localStorage
    const userRole = String(localStorage.getItem('userRole') || '').toLowerCase();
    const isManager = userRole === 'manager';

    const isProtectedRole = (roleName) => {
        const r = String(roleName || '').toLowerCase();
        return r === 'manager' || r === 'supervisor';
    };

    const canViewTarget = () => {
        if (!isProtectedRole(targetRoleName)) return true;
        return isManager;
    };

    const canEditRoleField = () => {
        if (isProtectedRole(targetRoleName)) return isManager;
        return true;
    };

    const canSeePasswordField = () => {
        if (isProtectedRole(targetRoleName)) return isManager;
        return isManager || userRole === 'supervisor';
    };

    const canSeeDeleteButton = () => {
        if (isProtectedRole(targetRoleName)) return isManager;
        return true;
    };

    useEffect(() => {
        const fetchTeamMember = async () => {
            try {
                const response = await axios.get(`/api/team/${id}`);
                const teamMember = response.data;

                setFirstCardOnRecord(!!teamMember.service_start_date);
                setFirstLabourCard(isStoredFirstCard(
                    toDateInput(teamMember.work_permit_issue_date),
                    toDateInput(teamMember.work_permit_expiry_date),
                ));
                setFormData({
                    first_name: teamMember.first_name,
                    middle_name: teamMember.middle_name || '',
                    last_name: teamMember.last_name,
                    email: teamMember.email,
                    role: teamMember.role,
                    eid: teamMember.eid || '',
                    phone_number: teamMember.phone_number || '',
                    calling_number: teamMember.calling_number || '',
                    created_at: toDateInput(teamMember.created_at),
                    does_have_id: !!teamMember.does_have_id,
                    does_have_id_from_company: !!teamMember.does_have_id_from_company,
                    is_trial: !!teamMember.is_trial,
                    password: '',
                    date_of_birth: toDateInput(teamMember.date_of_birth),
                    place_of_birth: teamMember.place_of_birth || '',
                    passport_number: teamMember.passport_number || '',
                    unified_number: teamMember.unified_number || '',
                    passport_issue_date: toDateInput(teamMember.passport_issue_date),
                    passport_expiry_date: toDateInput(teamMember.passport_expiry_date),
                    passport_issue_country: teamMember.passport_issue_country || '',
                    passport_issue_place: teamMember.passport_issue_place || '',
                    eid_expiry_date: toDateInput(teamMember.eid_expiry_date),
                    mohre_person_code: teamMember.mohre_person_code || '',
                    work_permit_number: teamMember.work_permit_number || '',
                    work_permit_issue_date: toDateInput(teamMember.work_permit_issue_date),
                    work_permit_expiry_date: toDateInput(teamMember.work_permit_expiry_date),
                    // Pre-fill from the labour-card issue date the first time (when
                    // not yet stored), matching how the issue date auto-fills.
                    service_start_date: toDateInput(teamMember.service_start_date || teamMember.work_permit_issue_date),
                    last_working_day: toDateInput(teamMember.last_working_day),
                    health_insurance_expiry: toDateInput(teamMember.health_insurance_expiry),
                    // keys absent entirely for non-managers (API strips them)
                    mohre_salary: teamMember.mohre_salary ?? '',
                    contractual_day_off:
                        teamMember.contractual_day_off === null || teamMember.contractual_day_off === undefined
                            ? ''
                            : String(teamMember.contractual_day_off),
                    conditional_bonus: teamMember.conditional_bonus ?? '',
                });

                const docsByType = {};
                (teamMember.documents || []).forEach((doc) => {
                    docsByType[doc.doc_type] = doc;
                });
                setDocuments(docsByType);

                // Replaced versions kept in Spaces — grouped per type, newest first.
                const archByType = {};
                (teamMember.document_archives || []).forEach((a) => {
                    (archByType[a.doc_type] = archByType[a.doc_type] || []).push(a);
                });
                setDocArchives(archByType);

                const roleNameLower = String(teamMember.role_name || '').toLowerCase();
                setTargetRoleName(roleNameLower);
                setIsArchived(teamMember.is_active === false);

                // ✅ block viewing manager/supervisor unless current user is manager
                if ((roleNameLower === 'manager' || roleNameLower === 'supervisor') && userRole !== 'manager') {
                    setAccessDenied(true);
                    onClose();
                    return;
                }
            } catch (error) {
                console.error('Error fetching team member details:', error);
                setError('Error fetching team member details.');
            }
        };

        const fetchRoles = async () => {
            try {
                const response = await axios.get('/api/team/roles');
                const roles = response.data;

                const filteredRoles = roles
                    .filter(role => {
                        if (role.role_name.toLowerCase() === 'client') return false;
                        if ((role.role_name.toLowerCase() === 'manager' || role.role_name.toLowerCase() === 'supervisor') && userRole !== 'manager') return false;
                        return true;
                    })
                    .map(role => ({
                        value: role.id,
                        label: role.role_name.charAt(0).toUpperCase() + role.role_name.slice(1),
                    }));

                setRoleOptions(filteredRoles);
            } catch (error) {
                console.error('Error fetching roles:', error);
                setError('Error fetching roles.');
            }
        };

        (async () => {
            await fetchTeamMember();
            if (!accessDenied) {
                await fetchRoles();
            }
        })();
        // onClose intentionally omitted: Team.js recreates it each render and it
        // must not retrigger the fetch
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, userRole, accessDenied]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'work_permit_expiry_date') {
            // Auto-fill the issue date from the expiry, and with no first-card date
            // on record, the first-card date too: it is saved from this form, and
            // would otherwise freeze on the issue date the form was opened with.
            const isFirstCard = looksLikeFirstCard(value, firstCardOnRecord);
            setFirstLabourCard(isFirstCard);
            const issue = deriveWorkPermitIssueDate(value, isFirstCard);
            setFormData((prev) => ({
                ...prev,
                work_permit_expiry_date: value,
                work_permit_issue_date: issue,
                ...(firstCardOnRecord ? {} : { service_start_date: issue }),
            }));
            return;
        }
        setFormData({ ...formData, [name]: value });
    };

    const handleFirstCardToggle = (e) => {
        const isFirstCard = e.target.checked;
        setFirstLabourCard(isFirstCard);
        setFormData((prev) => {
            const issue = deriveWorkPermitIssueDate(prev.work_permit_expiry_date, isFirstCard);
            return {
                ...prev,
                work_permit_issue_date: issue,
                ...(firstCardOnRecord ? {} : { service_start_date: issue }),
            };
        });
    };

    const handleRoleChange = (selectedOption) => {
        setFormData({ ...formData, role: selectedOption.value });
    };

    const handlePhoneChange = (field) => (value, meta) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (meta?.country?.dialCode) {
            setDialCodes((prev) => ({ ...prev, [field]: meta.country.dialCode }));
        }
        setFieldErrors((prev) => ({ ...prev, [field]: null }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: checked }));
    };

    // presign → PUT straight to the Space → record; the row saves immediately,
    // independent of the form's Save button.
    const handleDocumentSelect = (docType) => async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;

        setDocStatus((prev) => ({ ...prev, [docType]: { state: 'uploading' } }));
        try {
            const contentType = file.type || 'application/octet-stream';
            const presign = await axios.post(`/api/team/${id}/documents/presign`, {
                doc_type: docType,
                content_type: contentType,
            });
            await axios.put(presign.data.url, file, {
                // Spaces CORS has no Allow-Credentials; the global axios
                // withCredentials=true default would make the browser BLOCK
                // this cross-origin PUT — must be off for presigned uploads.
                withCredentials: false,
                headers: { 'Content-Type': contentType, 'x-amz-acl': 'public-read' },
            });
            // file_url is derived from file_key server-side — only send the key.
            const record = await axios.post(`/api/team/${id}/documents`, {
                doc_type: docType,
                file_key: presign.data.fileKey,
            });
            // The server archived the old version (kept in Spaces) — mirror that
            // locally so the history list updates without a refetch.
            const old = documents[docType];
            if (old?.file_url && old.file_url !== record.data.document.file_url) {
                setDocArchives((pa) => ({
                    ...pa,
                    [docType]: [{ doc_type: docType, file_url: old.file_url, archived_at: new Date().toISOString() }, ...(pa[docType] || [])],
                }));
            }
            setDocuments((prev) => ({ ...prev, [docType]: record.data.document }));
            setDocStatus((prev) => ({ ...prev, [docType]: { state: 'success' } }));
        } catch (err) {
            console.error(`Error uploading ${docType} document:`, err);
            setDocStatus((prev) => ({
                ...prev,
                [docType]: { state: 'error', message: err.response?.data?.error || 'Upload failed. Please try again.' },
            }));
        }
    };

    const handleUpdateMember = async (e) => {
        e.preventDefault();

        if (!canViewTarget()) {
            setError('Access denied.');
            return;
        }

        // Both numbers are ABSOLUTE requirements (2026-07-12): the form
        // cannot be submitted without them — legacy members get backfilled on
        // their next edit. First and last name likewise; middle name optional.
        const phoneErrors = {
            phone_number: getPhoneError('WhatsApp number', formData.phone_number, dialCodes.phone_number),
            calling_number: getPhoneError('Calling number', formData.calling_number, dialCodes.calling_number),
        };
        if (!String(formData.first_name || '').trim()) phoneErrors.first_name = 'First name is required';
        if (!String(formData.last_name || '').trim()) phoneErrors.last_name = 'Last name is required';
        if (phoneErrors.phone_number || phoneErrors.calling_number || phoneErrors.first_name || phoneErrors.last_name) {
            setFieldErrors(phoneErrors);
            setError('Please fix the highlighted required fields.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const cleanedFormData = {
                ...formData,
                email: formData.email.trim().toLowerCase(),
                phone_number: normalizePhoneForSubmit(formData.phone_number, dialCodes.phone_number),
                calling_number: normalizePhoneForSubmit(formData.calling_number, dialCodes.calling_number),
                // Only overwrite the First Labour Card date when explicitly unlocked;
                // otherwise the server keeps the stored value (fills it only if empty).
                override_service_start: overrideServiceStart,
            };
            // Manager-only fields never leave a non-manager session (the API
            // ignores them anyway).
            if (!isManager) {
                delete cleanedFormData.mohre_salary;
                delete cleanedFormData.contractual_day_off;
                delete cleanedFormData.conditional_bonus;
            }

            const response = await axios.put(`/api/team/${id}`, cleanedFormData);

            if (response.status === 200) {
                // The team PUT updates the profile default (used from next month).
                // When opened from Salaries with "this month" scope, also push the
                // new salary into this month's snapshot.
                if (payroll?.entryId && salaryScope === 'this' && isManager) {
                    try {
                        await axios.post(`/api/salaries/entries/${payroll.entryId}/salary`, {
                            mohre_salary: cleanedFormData.mohre_salary,
                            conditional_bonus: cleanedFormData.conditional_bonus,
                            scope: 'month',
                        });
                    } catch (e) { /* profile saved; this-month sync is best-effort */ }
                }
                onSaved();
            } else {
                setError('Error updating team member. Please try again.');
            }
        } catch (error) {
            console.error('Error updating team member:', error);
            // Surface the backend's specific message (duplicate WhatsApp number,
            // duplicate email, invalid format) so the user knows what to fix.
            setError(error.response?.data?.error || 'Something went wrong. Please try again.');
        }

        setLoading(false);
    };

    // Archive button → open the confirmation dialog (date prefilled with any
    // stored last working day, else today).
    const handleDeleteMember = () => {
        const today = new Date();
        const p = (n) => String(n).padStart(2, '0');
        setArchiveDate(formData.last_working_day || `${today.getFullYear()}-${p(today.getMonth() + 1)}-${p(today.getDate())}`);
        setError(null);
        setArchiveConfirm(true);
    };

    const confirmArchive = async () => {
        if (!archiveDate) return;
        setDeleting(true);
        try {
            // Persist the last working day first (normalised like a normal save).
            const cleaned = {
                ...formData,
                last_working_day: archiveDate,
                email: formData.email.trim().toLowerCase(),
                phone_number: normalizePhoneForSubmit(formData.phone_number, dialCodes.phone_number),
                calling_number: normalizePhoneForSubmit(formData.calling_number, dialCodes.calling_number),
                override_service_start: overrideServiceStart,
            };
            if (!isManager) {
                delete cleaned.mohre_salary;
                delete cleaned.conditional_bonus;
                delete cleaned.contractual_day_off;
            }
            await axios.put(`/api/team/${id}`, cleaned);
            const response = await axios.delete(`/api/team/${id}`);
            if (response.status === 200) {
                onSaved();
            } else {
                setArchiveConfirm(false);
                setError('Error archiving team member.');
            }
        } catch (error) {
            console.error('Error archiving team member:', error);
            setArchiveConfirm(false);
            setError('Something went wrong while archiving.');
        }
        setDeleting(false);
    };

    // Archived members get Unarchive where Delete normally sits.
    const handleUnarchiveMember = async () => {
        if (!window.confirm('Unarchive this team member?')) return;
        setDeleting(true);
        try {
            await axios.patch(`/api/team/${id}/unarchive`);
            onSaved();
        } catch (err) {
            console.error('Error unarchiving team member:', err);
            setError(err.response?.data?.error || 'Something went wrong while unarchiving.');
        }
        setDeleting(false);
    };

    // Driving license rows only apply to drivers — follow the currently
    // SELECTED role, falling back to the loaded one until options arrive.
    const selectedRoleOption = roleOptions.find((option) => option.value === formData.role);
    const isDriver = String(selectedRoleOption?.label || targetRoleName || '').toLowerCase() === 'driver';

    const renderField = (label, name, opts = {}) => (
        <div className="form-group">
            <label>
                {label}
                {opts.starred && <span className="req-star">✱</span>}
            </label>
            <input
                type={opts.type || 'text'}
                name={name}
                value={formData[name]}
                onChange={handleInputChange}
                step={opts.type === 'number' ? '0.01' : undefined}
                min={opts.type === 'number' ? '0' : undefined}
                required={opts.required}
                disabled={accessDenied || opts.disabled}
                className={fieldErrors[name] ? 'input-error' : undefined}
            />
            {opts.helper && <p className="form-helper">{opts.helper}</p>}
            {fieldErrors[name] && <p className="field-error">{fieldErrors[name]}</p>}
        </div>
    );

    const renderDocumentTile = ({ type, label, starred, guidelines, note, options, samples }) => {
        const doc = documents[type];
        const status = docStatus[type];
        const uploading = status?.state === 'uploading';

        return (
            <div key={type} className="doc-tile">
                <span className="doc-tile-label">
                    {label}
                    {starred && <span className="doc-req-star">✱</span>}
                </span>
                {(note || guidelines) && (
                    <span className="doc-tile-note">
                        {note}
                        {guidelines && <> <a className="doc-note-link" href={ICP_PHOTO_GUIDELINES_URL} target="_blank" rel="noreferrer">ICP photo guidelines (PDF)</a></>}
                    </span>
                )}
                <DocSampleBlock options={options} samples={samples} />
                <div className="doc-tile-status">
                    {uploading ? (
                        <span className="doc-status-uploading">Uploading…</span>
                    ) : status?.state === 'error' ? (
                        <span className="doc-status-error">{status.message}</span>
                    ) : doc ? (
                        <span className="doc-status-uploaded">
                            Uploaded · <a className="doc-view-link" href={doc.file_url} target="_blank" rel="noreferrer">View</a>
                        </span>
                    ) : (
                        <span className="doc-status-empty">Not uploaded</span>
                    )}
                </div>
                {(docArchives[type] || []).length > 0 && (
                    <details className="doc-history">
                        <summary>{docArchives[type].length} previous version{docArchives[type].length === 1 ? '' : 's'}</summary>
                        <ul>
                            {docArchives[type].map((a, i) => (
                                <li key={i}>
                                    <a className="doc-view-link" href={a.file_url} target="_blank" rel="noreferrer">View</a>
                                    <span className="doc-history-date">replaced {a.archived_at ? new Date(a.archived_at).toLocaleDateString('en-GB') : '—'}</span>
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
                <div className="doc-tile-actions">
                    <label className={`doc-upload-button${uploading || accessDenied ? ' doc-upload-disabled' : ''}`}>
                        {doc ? 'Replace' : 'Upload'}
                        <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={handleDocumentSelect(type)}
                            disabled={uploading || accessDenied}
                        />
                    </label>
                </div>
            </div>
        );
    };

    // Live while editing: every record still missing for this Company ID
    // employee (full set — their ID processing is already done).
    // Informational only — saving is never blocked.
    const cidMissing = companyIdMissingList(formData, (type) => !!documents[type], isDriver);

    return (
        <form className="add-team-member-form" onSubmit={handleUpdateMember}>
            <div className="team-passport-banner" role="note">
                <span className="team-passport-banner-icon" aria-hidden="true">📔</span>
                <div>
                    <p className="team-passport-banner-title">Enter all personal details EXACTLY as they appear in the passport.</p>
                    <p className="team-passport-banner-sub">Passport-mismatched details can void company ID processing and may result in financial loss.</p>
                </div>
            </div>

            {cidMissing.length > 0 && (
                <div className="cid-form-alert" role="alert">
                    <p className="cid-form-alert-title">⚠ Company ID staff — {cidMissing.length} record{cidMissing.length === 1 ? '' : 's'} still missing</p>
                    <ul className="cid-form-alert-list">
                        {cidMissing.map((label) => <li key={label}>{label}</li>)}
                    </ul>
                    <p className="cid-form-alert-note">You can still save — add these whenever they're available.</p>
                </div>
            )}

            <section className="team-form-section">
                <h4 className="team-form-section-title">Basic details</h4>
                <div className="team-form-grid team-form-grid-names">
                    {renderField('First Name', 'first_name', { required: true })}
                    {renderField('Middle Name', 'middle_name', { helper: 'Fill in if it appears on the passport.' })}
                    {renderField('Last Name', 'last_name', { required: true })}
                </div>
                <div className="team-form-grid">
                    {renderField('Email', 'email', { type: 'email' })}

                    {canEditRoleField() && (
                        <div className="form-group">
                            <label>Role</label>
                            <Select
                                classNamePrefix="team-select"
                                options={roleOptions}
                                onChange={handleRoleChange}
                                value={roleOptions.find(option => option.value === formData.role)}
                                placeholder="Select Role"
                                required
                                isDisabled={accessDenied}
                            />
                        </div>
                    )}

                    {renderField('First Working Day', 'created_at', { type: 'date', required: true })}

                    {canSeePasswordField() && (
                        <div className="form-group">
                            <label>Password (Optional)</label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Leave blank to keep existing"
                                disabled={accessDenied}
                            />
                        </div>
                    )}

                    <div className="form-group team-form-span-2">
                        <label>Identification status</label>
                        <div className="toggle-list">
                            <label className="toggle-row">
                                <span className="toggle-row-label">Has own Emirates ID</span>
                                <input
                                    type="checkbox"
                                    className="toggle-input"
                                    name="does_have_id"
                                    checked={formData.does_have_id}
                                    onChange={handleCheckboxChange}
                                    disabled={accessDenied}
                                />
                                <span className="toggle-track" aria-hidden="true">
                                    <span className="toggle-knob" />
                                </span>
                            </label>
                            <label className="toggle-row">
                                <span className="toggle-row-label">Company-provided ID</span>
                                <input
                                    type="checkbox"
                                    className="toggle-input"
                                    name="does_have_id_from_company"
                                    checked={formData.does_have_id_from_company}
                                    onChange={handleCheckboxChange}
                                    disabled={accessDenied}
                                />
                                <span className="toggle-track" aria-hidden="true">
                                    <span className="toggle-knob" />
                                </span>
                            </label>
                            <label className="toggle-row">
                                <span className="toggle-row-label">Trial staff</span>
                                <input
                                    type="checkbox"
                                    className="toggle-input"
                                    name="is_trial"
                                    checked={formData.is_trial}
                                    onChange={handleCheckboxChange}
                                    disabled={accessDenied}
                                />
                                <span className="toggle-track" aria-hidden="true">
                                    <span className="toggle-knob" />
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            </section>

            <section className="team-form-section">
                <h4 className="team-form-section-title">Contact</h4>
                <div className="team-form-grid">
                    <div className="form-group">
                        <label>WhatsApp Number</label>
                        <PhoneInput
                            defaultCountry="ae"
                            value={formData.phone_number}
                            onChange={handlePhoneChange('phone_number')}
                            placeholder="Enter WhatsApp number"
                            className={`client-phone-wrapper${fieldErrors.phone_number ? ' phone-input-error' : ''}`}
                            inputClassName="client-phone-input"
                            disabled={accessDenied}
                        />
                        {fieldErrors.phone_number && <p className="field-error">{fieldErrors.phone_number}</p>}
                    </div>
                    <div className="form-group">
                        <label>Calling Number</label>
                        <PhoneInput
                            defaultCountry="ae"
                            value={formData.calling_number}
                            onChange={handlePhoneChange('calling_number')}
                            placeholder="Enter calling number"
                            className={`client-phone-wrapper${fieldErrors.calling_number ? ' phone-input-error' : ''}`}
                            inputClassName="client-phone-input"
                            disabled={accessDenied}
                        />
                        {fieldErrors.calling_number && <p className="field-error">{fieldErrors.calling_number}</p>}
                    </div>
                </div>
            </section>

            <section className="team-form-section">
                <h4 className="team-form-section-title">Identity &amp; permits</h4>
                <p className="team-id-legend">
                    <span className="req-star">✱</span>
                    These fields and documents are required for company ID processing.
                </p>
                <div className="team-form-grid">
                    {renderField('Date of Birth', 'date_of_birth', { type: 'date', starred: true })}
                    {renderField('Place of Birth', 'place_of_birth', { starred: true })}
                    {renderField('Passport Number', 'passport_number', { starred: true })}
                    {renderField('Passport Issue Date', 'passport_issue_date', { type: 'date', starred: true })}
                    {renderField('Passport Expiry Date', 'passport_expiry_date', { type: 'date', starred: true })}
                    <div className="form-group">
                        <label>Nationality<span className="req-star">✱</span></label>
                        <Select
                            classNamePrefix="team-select"
                            options={COUNTRY_OPTIONS}
                            value={countryOptionFor(formData.passport_issue_country)}
                            onChange={(opt) => setFormData((prev) => ({ ...prev, passport_issue_country: opt ? opt.value : '' }))}
                            placeholder="Select nationality"
                            isClearable
                            isDisabled={accessDenied}
                        />
                    </div>
                    {renderField('Passport Issue Place', 'passport_issue_place', { starred: true })}
                    {renderField('Unified Number', 'unified_number', { starred: true, helper: (
                        <>
                            Find it on the residence/tourist visa, or in the{' '}
                            <a href="https://smartservices.icp.gov.ae/echannels/web/client/default.html#/fileValidity" target="_blank" rel="noreferrer">
                                ICP system by EID
                            </a>.
                        </>
                    ) })}
                    {renderField('Emirates ID', 'eid')}
                    {renderField('EID Expiry Date', 'eid_expiry_date', { type: 'date' })}
                    {renderField('MOHRE Person Code', 'mohre_person_code', { helper: 'On the labour card as "Person No"' })}
                    {renderField('Work Permit Number', 'work_permit_number', { helper: 'On the Labour Card' })}
                    {renderField('Work Permit Issue Date', 'work_permit_issue_date', { type: 'date', disabled: true, helper: issueDateHelper(firstLabourCard) })}
                    {renderField('Work Permit Expiry Date', 'work_permit_expiry_date', { type: 'date', helper: 'On the Labour Card' })}
                    <div className="form-group">
                        <label className="toggle-row">
                            <span className="toggle-row-label">First labour card (valid 2 months)</span>
                            <input
                                type="checkbox"
                                className="toggle-input"
                                checked={firstLabourCard}
                                onChange={handleFirstCardToggle}
                            />
                            <span className="toggle-track" aria-hidden="true">
                                <span className="toggle-knob" />
                            </span>
                        </label>
                    </div>
                    <div className="form-group">
                        <label>
                            First Labour Card from HHC
                            <button
                                type="button"
                                className="team-lock-btn"
                                title={overrideServiceStart ? 'Locked value — click to keep it fixed' : 'Override (use only if this date is wrong)'}
                                onClick={() => setOverrideServiceStart((o) => !o)}
                            >
                                {overrideServiceStart ? '🔓' : '🔒'}
                            </button>
                        </label>
                        <input
                            type="date"
                            name="service_start_date"
                            value={formData.service_start_date}
                            onChange={handleInputChange}
                            disabled={accessDenied || !overrideServiceStart}
                        />
                    </div>
                    {/* A stored date prorates the salary, so it is never hidden, archived or not. */}
                    {(isArchived || formData.last_working_day) && renderField('Last Working Day', 'last_working_day', { type: 'date', helper: 'Prorates the final month’s salary.' })}
                    {renderField('Health Insurance Expiry Date', 'health_insurance_expiry', { type: 'date' })}
                </div>
            </section>

            <section className="team-form-section">
                <h4 className="team-form-section-title">Documents</h4>
                <p className="team-form-section-note">Uploads save immediately — no need to press Save Changes.</p>
                <div className="doc-grid">
                    {DOCUMENT_ROWS
                        .filter((row) => !row.driverOnly || isDriver)
                        .map(renderDocumentTile)}
                </div>
            </section>

            {isManager && (
                <section className="team-form-section">
                    <h4 className="team-form-section-title">
                        Contract
                        <span className="team-section-pill">Manager only</span>
                    </h4>
                    <div className="team-form-grid">
                        {renderField('MOHRE Salary (AED)', 'mohre_salary', { type: 'number' })}
                        {renderField('Conditional Bonus (AED)', 'conditional_bonus', { type: 'number' })}

                        {/* Contractual, not a roster. Hours are flexible in practice;
                            this is the day the contract names, and what leave and
                            public-holiday entitlement are worked out against. */}
                        <div className="form-group">
                            <label>Contractual Day Off</label>
                            <select
                                name="contractual_day_off"
                                value={formData.contractual_day_off}
                                onChange={handleInputChange}
                                disabled={accessDenied}
                            >
                                <option value="">Not set</option>
                                {DAY_NAMES.map((d, i) => (
                                    <option key={d} value={String(i)}>{d}</option>
                                ))}
                            </select>
                            <p className="form-helper">
                                The day named in their contract. Hours are flexible in practice —
                                this is what holiday entitlement is worked out against.
                            </p>
                        </div>
                    </div>
                    {payroll && (
                        <div className="team-salary-scope">
                            <span className="team-salary-scope-label">Apply salary change to</span>
                            <label className={salaryScope === 'this' ? 'is-active' : ''}>
                                <input type="radio" name="salaryScope" checked={salaryScope === 'this'} onChange={() => setSalaryScope('this')} />
                                This month’s payroll{payroll.monthLabel ? ` (${payroll.monthLabel})` : ''} + going forward
                            </label>
                            <label className={salaryScope === 'next' ? 'is-active' : ''}>
                                <input type="radio" name="salaryScope" checked={salaryScope === 'next'} onChange={() => setSalaryScope('next')} />
                                From next month only (this month unchanged)
                            </label>
                        </div>
                    )}
                </section>
            )}

            <div className="team-form-footer">
                {error && <p className="team-form-footer-error">{error}</p>}
                {canSeeDeleteButton() && (isArchived ? (
                    <button
                        type="button"
                        className="team-unarchive-button"
                        onClick={handleUnarchiveMember}
                        disabled={deleting || accessDenied}
                    >
                        {deleting ? 'Unarchiving...' : 'Unarchive'}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="team-delete-button"
                        onClick={handleDeleteMember}
                        disabled={deleting || accessDenied}
                    >
                        {deleting ? 'Archiving...' : 'Archive'}
                    </button>
                ))}
                <button type="button" className="team-cancel-button" onClick={onClose} disabled={loading || deleting}>
                    Cancel
                </button>
                <button type="submit" className="team-save-button" disabled={loading || accessDenied}>
                    {loading ? 'Saving...' : 'Save Changes'}
                </button>
            </div>

            {/* Archive = ONE clear confirmation: pick the last working day, confirm. */}
            {archiveConfirm && (
                <div className="archive-confirm-overlay" onClick={(e) => { if (e.target === e.currentTarget && !deleting) setArchiveConfirm(false); }}>
                    <div className="archive-confirm">
                        <h3 className="archive-confirm-title">Archive {formData.first_name || 'this team member'}?</h3>
                        <p className="archive-confirm-text">
                            They leave the active roster and stop appearing in new payroll months,
                            but stay on past ones. Their final month prorates to the last working
                            day. You can unarchive later.
                        </p>
                        <label className="archive-confirm-label">
                            Last Working Day <span className="doc-req-star">✱</span>
                            <input
                                type="date"
                                className="archive-confirm-date"
                                value={archiveDate}
                                onChange={(e) => setArchiveDate(e.target.value)}
                                disabled={deleting}
                            />
                        </label>
                        <div className="archive-confirm-actions">
                            <button type="button" className="team-cancel-button" onClick={() => setArchiveConfirm(false)} disabled={deleting}>
                                Cancel
                            </button>
                            <button type="button" className="team-delete-button" onClick={confirmArchive} disabled={deleting || !archiveDate}>
                                {deleting ? 'Archiving…' : `Archive ${formData.first_name || 'member'}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </form>
    );
}

export default EditTeamMember;
